import { Link } from 'react-router-dom';
import { Wand2, Instagram } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.7a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.13z" />
    </svg>
  );
}

function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.4-5.96s-.36-.72-.36-1.78c0-1.66.97-2.9 2.17-2.9 1.02 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.01-.28 1.2.6 2.17 1.78 2.17 2.14 0 3.78-2.26 3.78-5.5 0-2.88-2.07-4.9-5.02-4.9-3.42 0-5.43 2.57-5.43 5.22 0 1.03.4 2.14.9 2.74a.36.36 0 0 1 .08.35l-.33 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.43-2.9-2.43-4.66 0-3.78 2.75-7.26 7.93-7.26 4.16 0 7.4 2.97 7.4 6.93 0 4.14-2.6 7.46-6.22 7.46-1.21 0-2.35-.63-2.74-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z" />
    </svg>
  );
}

export function Footer() {
  const { t } = useLanguage();

  const linkClass = "text-sm text-white/70 hover:text-primary transition-colors";
  const headingClass = "font-semibold text-white/80 text-xs uppercase tracking-widest mb-4";

  return (
    <footer className="bg-[#2D2D39]">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1 — Brand */}
          <div className="space-y-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-primary">
                {t('footer.brand')}
              </span>
            </Link>
            <p className="text-sm text-white/60 font-sans italic">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href="https://instagram.com/rubby" target="_blank" rel="noopener noreferrer" aria-label={t('header.followOn', { name: 'Instagram' })} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white/60 hover:text-white transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://tiktok.com/@rubby" target="_blank" rel="noopener noreferrer" aria-label={t('header.followOn', { name: 'TikTok' })} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white/60 hover:text-white transition-colors">
                <TikTokIcon className="w-5 h-5" />
              </a>
              <a href="https://pinterest.com/rubby" target="_blank" rel="noopener noreferrer" aria-label={t('header.followOn', { name: 'Pinterest' })} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white/60 hover:text-white transition-colors">
                <PinterestIcon className="w-5 h-5" />
              </a>
            </div>
            {/* Newsletter signup */}
            <div className="mt-6 space-y-2">
              <p className="text-white/70 text-sm font-medium">{t('footer.newsletter.title')}</p>
              <div className="flex">
                <input
                  type="email"
                  placeholder={t('footer.newsletter.emailPlaceholder')}
                  className="bg-white/10 border border-white/10 text-white placeholder:text-white/50 rounded-s-full px-4 py-2 text-sm flex-1 min-w-0 focus:outline-none focus:border-white/20"
                />
                <button className="bg-primary text-white rounded-e-full px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap">
                  {t('footer.newsletter.button')}
                </button>
              </div>
            </div>
          </div>

          {/* Column 2 — Create */}
          <div>
            <h3 className={headingClass}>{t('footer.create.title')}</h3>
            <ul className="space-y-2">
              <li><Link to="/toy?new=true" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.create.fromDrawing')}</Link></li>
              <li><Link to="/story?new=true" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.create.storybook')}</Link></li>
              <li><a href="#" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.create.giftCards')}</a></li>
            </ul>
          </div>

          {/* Column 3 — Learn More */}
          <div>
            <h3 className={headingClass}>{t('footer.learnMore.title')}</h3>
            <ul className="space-y-2">
              <li><Link to="/how-it-works" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.learnMore.howItWorks')}</Link></li>
              <li><Link to="/gallery" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.learnMore.gallery')}</Link></li>
              <li><Link to="/pricing" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.learnMore.pricing')}</Link></li>
              <li><Link to="/quality-safety" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.learnMore.qualitySafety')}</Link></li>
            </ul>
          </div>

          {/* Column 4 — Support */}
          <div>
            <h3 className={headingClass}>{t('footer.support.title')}</h3>
            <ul className="space-y-2">
              <li><Link to="/faq" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.support.faq')}</Link></li>
              <li><Link to="/contact" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.support.contact')}</Link></li>
              <li><Link to="/track" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.support.track')}</Link></li>
              <li><Link to="/reviews" className={`${linkClass} min-h-[44px] inline-flex items-center`}>{t('footer.support.reviews')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/60 font-sans">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-3 text-sm text-white/60">
            <Link to="/terms" className="hover:text-white/80 transition-colors">{t('footer.legal.terms')}</Link>
            <span className="text-white/40">·</span>
            <Link to="/privacy" className="hover:text-white/80 transition-colors">{t('footer.legal.privacy')}</Link>
            <span className="text-white/40">·</span>
            <a href="#" className="hover:text-white/80 transition-colors">{t('footer.legal.cookies')}</a>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-xs">
            <span>Visa</span>
            <span>·</span>
            <span>Mastercard</span>
            <span>·</span>
            <span>PayPal</span>
            <span>·</span>
            <span>Apple Pay</span>
          </div>
          <div
            className="shrink-0"
            dangerouslySetInnerHTML={{
              __html:
                "<a referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=8280545&Code=sctc1M68nKSvKOBtYCgK453ZRxKupfkX'><img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=8280545&Code=sctc1M68nKSvKOBtYCgK453ZRxKupfkX' alt='' style='cursor:pointer' code='sctc1M68nKSvKOBtYCgK453ZRxKupfkX'></a>",
            }}
          />
        </div>
      </div>
    </footer>
  );
}
